import React, { useState } from "react";
import { motion } from "framer-motion";
import { Search } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { CATEGORIES } from "../data/events";

export const MONTHS = [
  { id: "any", label: "Any month" },
  { id: "01", label: "January" },
  { id: "02", label: "February" },
  { id: "03", label: "March" },
  { id: "04", label: "April" },
  { id: "05", label: "May" },
  { id: "06", label: "June" },
  { id: "07", label: "July" },
  { id: "08", label: "August" },
  { id: "09", label: "September" },
  { id: "10", label: "October" },
  { id: "11", label: "November" },
  { id: "12", label: "December" },
];

const EASE = [0.76, 0, 0.24, 1];

export const SearchFilterBar = ({
  eventType,
  setEventType,
  month,
  setMonth,
  onSearch,
}) => {
  return (
    <section
      id="search"
      data-testid="search-section"
      className="relative bg-[color:var(--bt-bg)] px-6 md:px-10 -mt-24 md:-mt-32 z-20"
    >
      <div className="mx-auto max-w-[1400px]">
        <motion.div
          initial={{ opacity: 0, y: 60 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 1, ease: EASE }}
          data-testid="search-filter-bar"
          className="relative bt-square"
        >
          {/* Ornament kicker */}
          <div className="flex items-center gap-4 mb-5">
            <div className="h-px w-10 bg-[color:var(--bt-gold)]" />
            <span
              data-testid="search-eyebrow"
              className="text-[11px] tracking-widest-brand uppercase text-[color:var(--bt-gold)]"
            >
              Discover - Filter the season
            </span>
          </div>

          <div className="border border-[color:var(--bt-gold-line)] bg-[color:var(--bt-bg-2)]/85 backdrop-blur-xl">
            <div className="grid grid-cols-1 md:grid-cols-[1.1fr_1.1fr_auto] divide-y md:divide-y-0 md:divide-x divide-[color:var(--bt-gold-line)]">
              {/* Event Type */}
              <FilterCell label="Event Type" testId="filter-event-type-cell">
                <Select value={eventType} onValueChange={setEventType}>
                  <SelectTrigger
                    data-testid="filter-event-type"
                    className="rounded-none border-0 bg-transparent h-auto p-0 text-[color:var(--bt-ink)] text-xl md:text-2xl font-serif-display focus:ring-0 focus:ring-offset-0 shadow-none"
                  >
                    <SelectValue placeholder="All occasions" />
                  </SelectTrigger>
                  <SelectContent
                    data-testid="filter-event-type-content"
                    className="rounded-none border border-[color:var(--bt-gold-line)] bg-[color:var(--bt-bg-2)] text-[color:var(--bt-ink)]"
                  >
                    <SelectItem value="all" data-testid="filter-event-type-all">
                      All occasions
                    </SelectItem>
                    {CATEGORIES.map((c) => (
                      <SelectItem
                        key={c.id}
                        value={c.id}
                        data-testid={`filter-event-type-${c.id}`}
                      >
                        {c.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FilterCell>

              {/* Month */}
              <FilterCell label="Month" testId="filter-month-cell">
                <Select value={month} onValueChange={setMonth}>
                  <SelectTrigger
                    data-testid="filter-month"
                    className="rounded-none border-0 bg-transparent h-auto p-0 text-[color:var(--bt-ink)] text-xl md:text-2xl font-serif-display focus:ring-0 focus:ring-offset-0 shadow-none"
                  >
                    <SelectValue placeholder="Any month" />
                  </SelectTrigger>
                  <SelectContent
                    data-testid="filter-month-content"
                    className="rounded-none border border-[color:var(--bt-gold-line)] bg-[color:var(--bt-bg-2)] text-[color:var(--bt-ink)] max-h-72"
                  >
                    {MONTHS.map((m) => (
                      <SelectItem
                        key={m.id}
                        value={m.id}
                        data-testid={`filter-month-${m.id}`}
                      >
                        {m.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FilterCell>

              {/* CTA */}
              <button
                data-testid="filter-search-button"
                onClick={onSearch}
                className="group bg-[color:var(--bt-gold)] text-[color:var(--bt-bg)] px-8 md:px-12 py-6 md:py-0 flex items-center justify-center gap-3 hover:bg-[color:var(--bt-gold-bright)] transition-colors"
              >
                <Search size={16} strokeWidth={1.75} />
                <span className="text-[11px] tracking-widest-brand uppercase font-medium">
                  Discover
                </span>
              </button>
            </div>
          </div>

          <div className="mt-4 flex items-center justify-between text-[10px] tracking-widest-brand uppercase text-[color:var(--bt-ink-dim)]">
            <span data-testid="filter-hint">
              ⌘K - Or type a house, a gathering, a cause.
            </span>
            <span className="hidden md:inline">
              147 evenings on the ledger this season
            </span>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

const FilterCell = ({ label, testId, children }) => (
  <div
    data-testid={testId}
    className="px-6 py-5 md:px-10 md:py-7 flex flex-col gap-2 group hover:bg-[color:var(--bt-gold-glow)] transition-colors"
  >
    <span className="text-[10px] tracking-widest-brand uppercase text-[color:var(--bt-gold)]">
      {label}
    </span>
    {children}
  </div>
);

export default SearchFilterBar;
